import { useEffect, useMemo, useState } from "react";
import { fetchBrands, deleteBrand, updateBrand } from "../../api/brands";

function IconButton({
  title,
  onClick,
  disabled = false,
  tone = "default",
  loading = false,
  children,
}) {
  const toneClasses =
    tone === "danger"
      ? "text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
      : "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`flex h-9 w-9 items-center justify-center rounded-xl transition disabled:cursor-not-allowed disabled:opacity-50 ${toneClasses}`}
    >
      {loading ? (
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        children
      )}
    </button>
  );
}

function ActionModal({
  open,
  icon,
  title,
  description,
  children,
  onClose,
}) {
  if (!open) return null;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/45 px-4 backdrop-blur-sm dark:bg-black/65">
      <div
        className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-2xl dark:bg-slate-800">
            {icon}
          </div>
        </div>

        <h3 className="text-center text-lg font-semibold text-slate-900 dark:text-white">
          {title}
        </h3>

        {description && (
          <p className="mt-2 text-center text-sm leading-6 text-slate-500 dark:text-slate-400">
            {description}
          </p>
        )}

        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

export default function BrandsList({
  selectedBrand,
  onSelectBrand,
  onAddBrand,
  reloadKey,
}) {
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [deletingBrandId, setDeletingBrandId] = useState(null);
  const [brandToDelete, setBrandToDelete] = useState(null);

  const [editingBrandId, setEditingBrandId] = useState(null);
  const [brandToEdit, setBrandToEdit] = useState(null);
  const [editName, setEditName] = useState("");
  const [editError, setEditError] = useState("");

  useEffect(() => {
    let active = true;

    const loadBrands = async () => {
      try {
        setLoading(true);
        const data = await fetchBrands();
        if (!active) return;
        setBrands(Array.isArray(data) ? data : []);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadBrands();

    return () => {
      active = false;
    };
  }, [reloadKey]);

  const filteredBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;

    return brands.filter((brand) =>
      String(brand.name || "").toLowerCase().includes(q)
    );
  }, [brands, search]);

  const totalBrands = brands.length;
  const selectedBrandName = selectedBrand?.name || "None";

  const openDeleteModal = (brand, event) => {
    event.stopPropagation();
    setBrandToDelete(brand);
  };

  const closeDeleteModal = () => {
    if (deletingBrandId) return;
    setBrandToDelete(null);
  };

  const confirmDeleteBrand = async () => {
    if (!brandToDelete) return;

    try {
      setDeletingBrandId(brandToDelete.id);

      await deleteBrand(brandToDelete.id);

      setBrands((prev) => prev.filter((b) => b.id !== brandToDelete.id));

      if (selectedBrand?.id === brandToDelete.id) {
        onSelectBrand?.(null);
      }

      setBrandToDelete(null);
    } catch (error) {
      alert(error.message || "Failed to delete brand");
    } finally {
      setDeletingBrandId(null);
    }
  };

  const openEditModal = (brand, event) => {
    event.stopPropagation();
    setBrandToEdit(brand);
    setEditName(brand.name || "");
    setEditError("");
  };

  const closeEditModal = () => {
    if (editingBrandId) return;
    setBrandToEdit(null);
    setEditName("");
    setEditError("");
  };

  const confirmEditBrand = async () => {
    if (!brandToEdit?.id) return;

    const trimmed = editName.trim();

    if (!trimmed) {
      setEditError("Brand name is required");
      return;
    }

    try {
      setEditingBrandId(brandToEdit.id);
      setEditError("");

      await updateBrand({
        brandId: brandToEdit.id,
        name: trimmed,
      });

      setBrands((prev) =>
        prev.map((brand) =>
          brand.id === brandToEdit.id ? { ...brand, name: trimmed } : brand
        )
      );

      if (selectedBrand?.id === brandToEdit.id) {
        onSelectBrand?.({
          ...selectedBrand,
          name: trimmed,
        });
      }

      setBrandToEdit(null);
      setEditName("");
    } catch (error) {
      setEditError(error.message || "Failed to update brand");
    } finally {
      setEditingBrandId(null);
    }
  };

  return (
    <div className="relative flex h-full flex-col text-slate-900 dark:text-slate-100">
      {/* Toolbar */}
      <div className="space-y-4 border-b border-slate-200 p-4 dark:border-slate-800">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              Brand Directory
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {totalBrands} registered brand{totalBrands === 1 ? "" : "s"}
            </p>
          </div>

          <button
            type="button"
            onClick={onAddBrand}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-black px-4 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 dark:bg-white dark:text-black"
          >
            <span className="text-base leading-none">＋</span>
            Add
          </button>
        </div>

        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
            🔎
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search brands..."
            className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Total: {totalBrands}
          </div>
          <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Selected: {selectedBrandName}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex h-full min-h-[220px] flex-col items-center justify-center text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-700 dark:border-slate-700 dark:border-t-slate-200" />
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              Loading brands...
            </p>
          </div>
        ) : filteredBrands.length === 0 ? (
          <div className="flex h-full min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 text-center dark:border-slate-800 dark:bg-slate-950/20">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-2xl dark:bg-slate-800">
              🏷️
            </div>
            <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
              No brands found
            </h4>
            <p className="mt-2 max-w-xs text-sm leading-6 text-slate-500 dark:text-slate-400">
              {search.trim()
                ? "No brands match your current search."
                : "No brands have been added yet. Create one to begin organizing models."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredBrands.map((brand) => {
              const isSelected = selectedBrand?.id === brand.id;
              const isDeleting = deletingBrandId === brand.id;
              const isEditing = editingBrandId === brand.id;

              return (
                <button
                  key={brand.id}
                  type="button"
                  onClick={() => onSelectBrand(brand)}
                  className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                    isSelected
                      ? "border-slate-900 bg-slate-900 text-white shadow-sm dark:border-white dark:bg-white dark:text-black"
                      : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700 dark:hover:bg-slate-800/60"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex h-9 w-9 items-center justify-center rounded-xl text-sm font-semibold ${
                            isSelected
                              ? "bg-white/15 text-white dark:bg-black/10 dark:text-black"
                              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          }`}
                        >
                          {String(brand.name || "?").charAt(0).toUpperCase()}
                        </span>

                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">
                            {brand.name}
                          </p>
                          <p
                            className={`mt-1 text-xs ${
                              isSelected
                                ? "text-white/70 dark:text-black/70"
                                : "text-slate-500 dark:text-slate-400"
                            }`}
                          >
                            Brand catalog
                          </p>
                        </div>
                      </div>
                    </div>

                    <div
                      className="flex shrink-0 items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        title={`Edit ${brand.name}`}
                        onClick={(e) => openEditModal(brand, e)}
                        disabled={isDeleting || isEditing}
                        loading={isEditing}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="h-4 w-4"
                        >
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.96 1.96 3.75 3.75 2.13-1.79z" />
                        </svg>
                      </IconButton>

                      <IconButton
                        title={`Delete ${brand.name}`}
                        onClick={(e) => openDeleteModal(brand, e)}
                        disabled={isDeleting || isEditing}
                        loading={isDeleting}
                        tone="danger"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="h-4 w-4"
                        >
                          <path d="M6 7h12l-1 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 7zm3-4h6l1 2h4v2H4V5h4l1-2z" />
                        </svg>
                      </IconButton>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit modal */}
      <ActionModal
        open={!!brandToEdit}
        icon="✏️"
        title="Edit Brand"
        description={
          brandToEdit
            ? `Update the name for ${brandToEdit.name}.`
            : ""
        }
        onClose={closeEditModal}
      >
        <div className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Brand name
            </label>
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Enter brand name"
              disabled={!!editingBrandId}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 disabled:opacity-70 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
            />
            {editError && (
              <p className="mt-2 text-sm text-red-500 dark:text-red-400">
                {editError}
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={closeEditModal}
              disabled={!!editingBrandId}
              className="flex-1 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={confirmEditBrand}
              disabled={!!editingBrandId}
              className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-black px-4 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-70 dark:bg-white dark:text-black"
            >
              {editingBrandId === brandToEdit?.id ? (
                <>
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent dark:border-black" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </ActionModal>

      {/* Delete modal */}
      <ActionModal
        open={!!brandToDelete}
        icon="🗑️"
        title="Delete Brand?"
        description={
          brandToDelete
            ? `You are about to delete ${brandToDelete.name} and all its models. This action cannot be undone.`
            : ""
        }
        onClose={closeDeleteModal}
      >
        <div className="flex gap-3">
          <button
            type="button"
            onClick={closeDeleteModal}
            disabled={!!deletingBrandId}
            className="flex-1 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={confirmDeleteBrand}
            disabled={!!deletingBrandId}
            className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-70"
          >
            {deletingBrandId === brandToDelete?.id ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Deleting...
              </>
            ) : (
              "Delete Brand"
            )}
          </button>
        </div>
      </ActionModal>
    </div>
  );
}